var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faBanParking, faCircleParking } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox, FormControlLabel, Link, Stack, Typography, styled } from '@mui/material';
import { chain, chunk as chunkFromLodash, mean, meanBy, round, sumBy, times } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PlanType, ShrinkageCategory, StatusPermission, TABLE_HIERARCHY_COLORS, } from '../../../enum';
import { RumEntityLabel, rumEntityName } from '../../../observability/rumEntity';
import { Autocomplete, FormField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, } from '../../common';
import { ExpandableRowLabel, TableHierarchyCell } from '../../common/tableHierarchy';
import { useFormlessContext } from '../../hooks';
import { formatTableHeaderReport } from '../../report/util';
import { QuickInputTrigger } from '../locationTable/header';
import { useDetailTimeline, useEffectiveCapacityPermission, useIsPlanLocked, usePeriodChunkSize, usePeriods, usePlanType, } from '../selectors';
import { EditShrinkageFactorDialog } from './editFactorDialog';
import { ShrinkageQuickInputDialog } from './quickInputDialog';
import { useShrinkageTable } from './selectors';
import { ShrinkageUnitInput } from './shrinkageUnitInput';
import { SHRINKAGE_DECIMAL, ShrinkageUnitMode, getBusinessDays, getShrinkageDisplayValue, useShrinkageStaffSettings, useUnitOptions, } from './useShrinkageUnit';
var STICKIES = ['18rem'];
var CATEGORY_ORDER = ['out_of_office', 'in_office', 'uncategorized'];
var EMPTY_CATEGORY_ORDER = ['out_of_office', 'in_office'];
var PLAIN_ROWS_KEY = '__PLAIN_ROWS__';
var stripDisplaySuffix = function (name) { return (name !== null && name !== void 0 ? name : '').replace(/\s\+$/, '').trim(); };
var getShrinkageCategory = function (category) {
    return category === ShrinkageCategory.OutOfOffice || category === ShrinkageCategory.InOffice ? category : null;
};
var normalizeQueueLocationId = function (queueLocationId) { return queueLocationId !== null && queueLocationId !== void 0 ? queueLocationId : null; };
var normalizeSubcategory = function (subcategory) { var _a; return (_a = subcategory === null || subcategory === void 0 ? void 0 : subcategory.trim()) !== null && _a !== void 0 ? _a : ''; };
var getShrinkageIdentityKey = function (shrinkage) {
    var _a, _b, _c;
    return "".concat((_a = shrinkage.shrinkageCategory) !== null && _a !== void 0 ? _a : '', "|").concat(normalizeSubcategory(shrinkage.shrinkageSubcategory), "|").concat((_b = shrinkage.name) !== null && _b !== void 0 ? _b : '', "|").concat((_c = shrinkage.shrinkageType) !== null && _c !== void 0 ? _c : '');
};
var getShrinkageDisplayKey = function (identityKey, queueLocationId) { var _a; return "".concat((_a = normalizeQueueLocationId(queueLocationId)) !== null && _a !== void 0 ? _a : 'queue', "|").concat(identityKey); };
var hasSameShrinkageId = function (left, right) {
    return left.id !== undefined && right.id !== undefined && left.id === right.id;
};
var hasSameShrinkageIdentity = function (left, right) {
    var _a, _b, _c, _d;
    return !!left.name &&
        left.name === right.name &&
        ((_a = left.shrinkageCategory) !== null && _a !== void 0 ? _a : null) === ((_b = right.shrinkageCategory) !== null && _b !== void 0 ? _b : null) &&
        normalizeSubcategory(left.shrinkageSubcategory) === normalizeSubcategory(right.shrinkageSubcategory) &&
        ((_c = left.shrinkageType) !== null && _c !== void 0 ? _c : null) === ((_d = right.shrinkageType) !== null && _d !== void 0 ? _d : null);
};
var isSameShrinkage = function (left, right) {
    return !!left &&
        !!right &&
        normalizeQueueLocationId(left.queueLocationId) === normalizeQueueLocationId(right.queueLocationId) &&
        (hasSameShrinkageId(left, right) || hasSameShrinkageIdentity(left, right));
};
var findShrinkageFormRowIndex = function (monthGroup, cell) { var _a; return (_a = monthGroup === null || monthGroup === void 0 ? void 0 : monthGroup.findIndex(function (item) { return isSameShrinkage(item, cell); })) !== null && _a !== void 0 ? _a : -1; };
var isShrinkageCell = function (cell) { return 'name' in cell; };
var getCellQueueLocationId = function (cell) {
    return isShrinkageCell(cell) ? cell.queueLocationId : undefined;
};
var TableCellTotal = styled(TableCell, {
    shouldForwardProp: function (prop) { return prop !== 'isSubtotal'; },
})(function (_a) {
    var theme = _a.theme, isSubtotal = _a.isSubtotal;
    return ({
        '&&': {
            color: "".concat(isSubtotal ? theme.palette.common.white : theme.palette.text.primary, " !important"),
            backgroundColor: "".concat(isSubtotal ? theme.palette.text.primary : theme.palette.primary.light, " !important"),
            fontWeight: 700,
        },
    });
});
var StyledTableCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        minWidth: theme.typography.pxToRem(100),
    });
});
var HeaderCheckbox = styled(Checkbox)(function (_a) {
    var theme = _a.theme;
    return ({
        padding: theme.spacing(0.25),
        color: theme.palette.common.white,
        '&.Mui-checked': {
            color: theme.palette.common.white,
        },
    });
});
var CategoryRowCell = styled(TableHierarchyCell)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: "".concat(theme.palette.primary.light, " !important"),
        fontWeight: 700,
        fontSize: theme.typography.body2.fontSize,
    });
});
var CategoryRowValueCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: "".concat(theme.palette.primary.light, " !important"),
    });
});
var SubcategoryHeaderCell = styled(TableHierarchyCell)(function () { return ({
    backgroundColor: "".concat(TABLE_HIERARCHY_COLORS.lightBlueGray, " !important"),
}); });
var SubcategoryTotalCell = styled(TableCell)(function () { return ({
    backgroundColor: "".concat(TABLE_HIERARCHY_COLORS.lightBlueGray, " !important"),
}); });
var FactorNameCell = styled(TableHierarchyCell, {
    shouldForwardProp: function (prop) { return prop !== 'isSubFactor'; },
})(function (_a) {
    var isSubFactor = _a.isSubFactor;
    return ({
        backgroundColor: "".concat(isSubFactor ? TABLE_HIERARCHY_COLORS.lightGray : TABLE_HIERARCHY_COLORS.lightBlueGray, " !important"),
    });
});
var UnitModeContainer = styled(Box)({
    position: 'relative',
    overflow: 'visible',
});
var UnitModeAutocomplete = styled(Autocomplete)({
    position: 'absolute',
    top: '50%',
    left: '50%',
    maxWidth: '16rem',
    transform: 'translate(-50%, -50%)',
});
var StyledLink = styled(Link, {
    shouldForwardProp: function (prop) { return prop !== 'disabled' && prop !== 'showCheckbox'; },
})(function (_a) {
    var disabled = _a.disabled;
    return ({
        border: 0,
        cursor: disabled ? 'default' : 'pointer',
        padding: 0,
        textAlign: 'left',
        display: 'inline',
    });
});
var getDisplaySuffix = function (unitMode) {
    if (unitMode === ShrinkageUnitMode.Days || unitMode === ShrinkageUnitMode.Hours)
        return undefined;
    return '%';
};
var getEmptyShrinkageTotalData = function (columnCount) {
    if (columnCount === void 0) { columnCount = 0; }
    return times(columnCount, function (index) { return ({ month: index, displayValue: 0 }); });
};
var getShrinkageTotalIdentity = function (values, fallbackQueueLocationId) {
    var _a, _b, _c;
    var firstMonthCell = (_a = values.find(function (cell) { return isShrinkageCell(cell) && cell.month % 1 === 0; })) !== null && _a !== void 0 ? _a : values.find(isShrinkageCell);
    if (!firstMonthCell)
        return null;
    return {
        name: firstMonthCell.name,
        queueLocationId: (_c = (_b = firstMonthCell.queueLocationId) !== null && _b !== void 0 ? _b : fallbackQueueLocationId) !== null && _c !== void 0 ? _c : null,
        shrinkageCategory: firstMonthCell.shrinkageCategory,
        shrinkageSubcategory: firstMonthCell.shrinkageSubcategory,
        shrinkageType: firstMonthCell.shrinkageType,
    };
};
var hasSameShrinkageTotalIdentity = function (left, right) {
    return !!left &&
        normalizeQueueLocationId(left.queueLocationId) === normalizeQueueLocationId(right.queueLocationId) &&
        hasSameShrinkageIdentity(left, right);
};
var getShrinkageItems = function (group, dataRowIndices, dataRowIdentities) {
    if (dataRowIdentities) {
        return group.filter(function (item) { return dataRowIdentities.some(function (id) { return hasSameShrinkageTotalIdentity(item, id); }); });
    }
    if (dataRowIndices === undefined) {
        return group;
    }
    return dataRowIndices.map(function (ri) { return group[ri]; });
};
var getShrinkageDisplayTotal = function (group, dataRowIndices, dataRowIdentities, toDisplayValue) {
    var items = dataRowIdentities
        ? dataRowIdentities.map(function (identity) { return group.find(function (g) { return hasSameShrinkageTotalIdentity(g, identity); }); })
        : getShrinkageItems(group, dataRowIndices);
    return sumBy(items, function (item) { var _a; return round(toDisplayValue(Number((_a = item === null || item === void 0 ? void 0 : item.percent) !== null && _a !== void 0 ? _a : 0)), SHRINKAGE_DECIMAL); });
};
var getShrinkageTotalData = function (watchShrinkage, dataRowIndices, dataRowIdentities, columnCount, isOverallTotal, toDisplayValue) {
    if (dataRowIdentities === undefined ? (dataRowIndices === null || dataRowIndices === void 0 ? void 0 : dataRowIndices.length) === 0 : dataRowIdentities.length === 0) {
        return getEmptyShrinkageTotalData(columnCount);
    }
    var totalData = chain(watchShrinkage)
        .map(function (group, index) {
        if (isOverallTotal && group) {
            var items = getShrinkageItems(group, dataRowIndices, dataRowIdentities);
            var oos = sumBy(items.filter(function (x) { return (x === null || x === void 0 ? void 0 : x.shrinkageCategory) === ShrinkageCategory.OutOfOffice; }), function (item) { var _a; return Number((_a = item === null || item === void 0 ? void 0 : item.percent) !== null && _a !== void 0 ? _a : 0); });
            var ios = sumBy(items.filter(function (x) { return (x === null || x === void 0 ? void 0 : x.shrinkageCategory) === ShrinkageCategory.InOffice; }), function (item) { var _a; return Number((_a = item === null || item === void 0 ? void 0 : item.percent) !== null && _a !== void 0 ? _a : 0); });
            var percent = oos + ios - (oos * ios) / 100;
            return { month: index, displayValue: toDisplayValue(percent, index) };
        }
        var displayValue = group
            ? getShrinkageDisplayTotal(group, dataRowIndices, dataRowIdentities, function (percent) {
                return toDisplayValue(percent, index);
            })
            : 0;
        return { month: index, displayValue: displayValue };
    })
        .value();
    return totalData.length ? totalData : getEmptyShrinkageTotalData(columnCount);
};
var useShrinkageRowDisplayChunks = function (watchShrinkage, dataRowIndices, dataRowIdentities, unitMode, paidHoursPerDay, workingDaysPerWeek, isOverallTotal) {
    var chunkSize = usePeriodChunkSize();
    var periods = usePeriods();
    var planType = usePlanType();
    var shrinkageFooterData = getShrinkageTotalData(watchShrinkage, dataRowIndices, dataRowIdentities, periods === null || periods === void 0 ? void 0 : periods.length, isOverallTotal, function (percent, month) {
        var _a;
        var businessDays = getBusinessDays(planType, workingDaysPerWeek !== null && workingDaysPerWeek !== void 0 ? workingDaysPerWeek : 0, periods, month);
        return ((_a = getShrinkageDisplayValue(percent, unitMode !== null && unitMode !== void 0 ? unitMode : ShrinkageUnitMode.Percent, paidHoursPerDay !== null && paidHoursPerDay !== void 0 ? paidHoursPerDay : 0, businessDays)) !== null && _a !== void 0 ? _a : 0);
    });
    return chunkFromLodash(shrinkageFooterData, chunkSize);
};
var renderShrinkageValueCells = function (CellComponent, chunks, isIncludeAverage, unitMode, label, extraCellProps) {
    return chunks.flatMap(function (chunk, chunkIdx) {
        var isLastChunk = chunkIdx === chunks.length - 1;
        return __spreadArray(__spreadArray([], chunk.map(function (item, idx) { return (_jsx(CellComponent, __assign({ value: item.displayValue, decimal: SHRINKAGE_DECIMAL, suffix: getDisplaySuffix(unitMode), highlightBorder: !isIncludeAverage && idx === chunk.length - 1 && !isLastChunk }, extraCellProps), item.month)); }), true), (isIncludeAverage
            ? [
                _jsx(CellComponent, __assign({ value: meanBy(chunk, function (x) { return Number(x.displayValue); }), decimal: SHRINKAGE_DECIMAL, suffix: getDisplaySuffix(unitMode), highlightBorder: !isLastChunk }, extraCellProps), "avg-".concat(label, "-").concat(chunkIdx)),
            ]
            : []), true);
    });
};
export var ShrinkageTotalRow = function (_a) {
    var addColumns = _a.addColumns, label = _a.label, watchShrinkage = _a.watchShrinkage, dataRowIndices = _a.dataRowIndices, dataRowIdentities = _a.dataRowIdentities, _b = _a.isSubtotal, isSubtotal = _b === void 0 ? false : _b, unitMode = _a.unitMode, paidHoursPerDay = _a.paidHoursPerDay, workingDaysPerWeek = _a.workingDaysPerWeek;
    var isIncludeAverage = !!(addColumns === null || addColumns === void 0 ? void 0 : addColumns.includes('Average'));
    var chunks = useShrinkageRowDisplayChunks(watchShrinkage, dataRowIndices, dataRowIdentities, unitMode, paidHoursPerDay, workingDaysPerWeek, !isSubtotal);
    return (_jsxs(TableRow, { children: [_jsx(TableCellTotal, { stickySizes: STICKIES, component: "th", align: "left", value: label, isSubtotal: isSubtotal }), renderShrinkageValueCells(TableCellTotal, chunks, isIncludeAverage, unitMode, label, { isSubtotal: isSubtotal })] }));
};
var ShrinkageSubcategoryTotalRow = function (_a) {
    var addColumns = _a.addColumns, label = _a.label, watchShrinkage = _a.watchShrinkage, dataRowIndices = _a.dataRowIndices, dataRowIdentities = _a.dataRowIdentities, unitMode = _a.unitMode, paidHoursPerDay = _a.paidHoursPerDay, workingDaysPerWeek = _a.workingDaysPerWeek, disabled = _a.disabled, isReview = _a.isReview, onEditFactor = _a.onEditFactor, factorTarget = _a.factorTarget;
    var isIncludeAverage = !!(addColumns === null || addColumns === void 0 ? void 0 : addColumns.includes('Average'));
    var chunks = useShrinkageRowDisplayChunks(watchShrinkage, dataRowIndices, dataRowIdentities, unitMode, paidHoursPerDay, workingDaysPerWeek, false);
    var handleEditFactor = function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (disabled || !factorTarget)
            return;
        onEditFactor === null || onEditFactor === void 0 ? void 0 : onEditFactor(factorTarget);
    };
    var factorNameLink = isReview || disabled ? (_jsx(Typography, __assign({ sx: { fontWeight: 'normal' }, variant: "subtitle2" }, { children: label }))) : (_jsx(StyledLink, __assign({ type: "button", underline: "always", onClick: handleEditFactor, sx: { fontWeight: 'normal' }, variant: "subtitle2" }, { children: label })));
    return (_jsxs(TableRow, { children: [_jsx(SubcategoryHeaderCell, __assign({ stickySizes: STICKIES, align: "left", nestLevel: 2 }, { children: factorNameLink })), renderShrinkageValueCells(SubcategoryTotalCell, chunks, isIncludeAverage, unitMode, label)] }));
};
var useAllFormRowIndices = function (queueLocationId) {
    var shrinkageData = useShrinkageTable(queueLocationId);
    var methods = useFormlessContext();
    var watchShrinkageData = useWatch({ name: 'shrinkages', control: methods === null || methods === void 0 ? void 0 : methods.control });
    return useMemo(function () {
        var _a;
        var indices = [];
        for (var _i = 0, _b = Object.keys(shrinkageData); _i < _b.length; _i++) {
            var key = _b[_i];
            var firstCell = (_a = shrinkageData[key]) === null || _a === void 0 ? void 0 : _a.find(function (cell) { return cell.month % 1 === 0; });
            if (!firstCell)
                continue;
            var formIdx = findShrinkageFormRowIndex(watchShrinkageData === null || watchShrinkageData === void 0 ? void 0 : watchShrinkageData[firstCell.month], firstCell);
            if (formIdx >= 0)
                indices.push(formIdx);
        }
        return indices;
    }, [shrinkageData, watchShrinkageData]);
};
var ShrinkageTableHeader = function (_a) {
    var _b, _c;
    var _d = _a.addColumns, addColumns = _d === void 0 ? [] : _d, showCheckbox = _a.showCheckbox, selectAll = _a.selectAll, handleSelectAllChange = _a.handleSelectAllChange, disabled = _a.disabled, onQuickInputClick = _a.onQuickInputClick, isReview = _a.isReview, unitMode = _a.unitMode, setUnitMode = _a.setUnitMode, isUnitModeEnabled = _a.isUnitModeEnabled;
    var t = useTranslation('wizard', { keyPrefix: 'shrinkage' }).t;
    var planType = usePlanType();
    var timelines = useDetailTimeline.apply(void 0, addColumns);
    var groupSize = 12 + addColumns.length;
    var unitOptions = useUnitOptions();
    var unitModeSelector = (_jsx(UnitModeContainer, { children: _jsx(UnitModeAutocomplete, { size: "small", options: unitOptions, getOptionValue: function (o) { return o.id; }, value: unitMode, onChange: function (v) { return setUnitMode(v !== null && v !== void 0 ? v : ShrinkageUnitMode.Percent); }, disabled: !isUnitModeEnabled }) }));
    return (_jsxs(_Fragment, { children: [timelines === null || timelines === void 0 ? void 0 : timelines.map(function (_a, rowIdx) {
                var titles = _a[0], type = _a[1];
                var Component = rowIdx ? TableBody : TableHead;
                return (_jsx(Component, { children: _jsxs(TableRow, { children: [_jsxs(TableCell, __assign({ greyColor: !!rowIdx, stickySizes: STICKIES, align: "center" }, { children: [rowIdx === 0 && (_jsxs(Stack, __assign({ direction: "row", alignItems: "center", justifyContent: "center", spacing: 1 }, { children: [showCheckbox && (_jsx(FormControlLabel, { control: _jsx(HeaderCheckbox, { size: "small", checked: selectAll, onChange: handleSelectAllChange }), label: _jsx(Typography, __assign({ sx: { ml: 2 }, fontWeight: "bold" }, { children: t('selectAll') })) })), onQuickInputClick && !isReview && (_jsx(QuickInputTrigger, { disabled: disabled, onClick: onQuickInputClick }))] }))), rowIdx === 1 && unitModeSelector] })), titles === null || titles === void 0 ? void 0 : titles.map(function (title, cellIdx, arr) { return (_jsx(TableCell, __assign({ greyColor: !!rowIdx, sx: { whiteSpace: 'nowrap', minWidth: '5rem' }, highlightBorder: planType === PlanType.Monthly && !((cellIdx + 1) % groupSize) && cellIdx !== arr.length - 1 }, { children: formatTableHeaderReport(title) }), "".concat(title, "-").concat(cellIdx))); })] }) }, type));
            }), timelines.length < 2 && (_jsx(TableBody, { children: _jsxs(TableRow, { children: [_jsx(TableCell, __assign({ greyColor: true, stickySizes: STICKIES, align: "center" }, { children: unitModeSelector })), ((_c = (_b = timelines[0]) === null || _b === void 0 ? void 0 : _b[0]) !== null && _c !== void 0 ? _c : []).map(function (title, cellIdx, titlesArr) { return (_jsx(TableCell, { greyColor: true, highlightBorder: planType === PlanType.Monthly && !((cellIdx + 1) % groupSize) && cellIdx !== titlesArr.length - 1 }, "".concat(title, "-").concat(cellIdx))); })] }) }))] }));
};
var ShrinkageCategoryRow = function (_a) {
    var categoryLabel = _a.categoryLabel, columnCount = _a.columnCount, _b = _a.isExpanded, isExpanded = _b === void 0 ? true : _b, onToggle = _a.onToggle, hasChildren = _a.hasChildren, chunkSize = _a.chunkSize, isIncludeAverage = _a.isIncludeAverage;
    return (_jsxs(TableRow, { children: [_jsx(CategoryRowCell, __assign({ stickySizes: STICKIES, component: "th", align: "left", nestLevel: 1, sx: { py: 2 } }, { children: _jsx(Box, __assign({ "data-testid": hasChildren ? 'shrinkage-category-toggle-icon' : undefined }, { children: _jsx(ExpandableRowLabel, __assign({ expanded: isExpanded, onToggle: hasChildren ? onToggle : undefined, bold: true }, { children: _jsx(Typography, __assign({ fontWeight: "bold" }, { children: categoryLabel })) })) })) })), times(columnCount, function (index) {
                var isAverageColumn = isIncludeAverage && (index + 1) % (chunkSize + 1) === 0;
                return (_jsx(CategoryRowValueCell, { highlightBorder: index !== columnCount - 1 && (isIncludeAverage ? isAverageColumn : !((index + 1) % chunkSize)) }, index));
            })] }));
};
var ShrinkageTableBody = function (_a) {
    var _b, _c, _d;
    var disabled = _a.disabled, _f = _a.showCheckbox, showCheckbox = _f === void 0 ? false : _f, selectedRows = _a.selectedRows, setSelectedRows = _a.setSelectedRows, addColumns = _a.addColumns, queueLocationId = _a.queueLocationId, onEditFactor = _a.onEditFactor, isReview = _a.isReview, _g = _a.unitMode, unitMode = _g === void 0 ? ShrinkageUnitMode.Percent : _g;
    var _h = useShrinkageStaffSettings(queueLocationId), paidHoursPerDay = _h.paidHoursPerDay, workingDaysPerWeek = _h.workingDaysPerWeek;
    var planType = usePlanType();
    var shrinkageData = useShrinkageTable(queueLocationId);
    var chunkSize = usePeriodChunkSize();
    var timelines = useDetailTimeline.apply(void 0, (addColumns !== null && addColumns !== void 0 ? addColumns : []));
    var columnCount = (_d = (_c = (_b = timelines.at(-1)) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0;
    var methods = useFormlessContext();
    var watchShrinkageData = useWatch({ name: 'shrinkages', control: methods === null || methods === void 0 ? void 0 : methods.control });
    var periods = usePeriods();
    var watchShrinkageByName = useMemo(function () {
        var result = {};
        var _loop_1 = function (key) {
            var factorShrinkages = (watchShrinkageData || []).map(function (monthGroup) {
                return (monthGroup || []).find(function (shrinkage) {
                    return getShrinkageIdentityKey(shrinkage) === key &&
                        normalizeQueueLocationId(shrinkage.queueLocationId) === normalizeQueueLocationId(queueLocationId);
                });
            });
            result[key] = chunkFromLodash(factorShrinkages.map(function (s) { var _a; return Number((_a = s === null || s === void 0 ? void 0 : s.percent) !== null && _a !== void 0 ? _a : 0); }), chunkSize);
        };
        for (var _i = 0, _a = Object.keys(shrinkageData); _i < _a.length; _i++) {
            var key = _a[_i];
            _loop_1(key);
        }
        return result;
    }, [shrinkageData, watchShrinkageData, queueLocationId, chunkSize]);
    var isIncludeAverage = addColumns === null || addColumns === void 0 ? void 0 : addColumns.includes('Average');
    var t = useTranslation('wizard', { keyPrefix: 'shrinkage' }).t;
    var shrinkageDataWithTotal = useMemo(function () {
        return chain(shrinkageData)
            .mapValues(function (group, key) {
            return chain(group)
                .chunk(chunkSize)
                .flatMap(function (chunk, index) {
                if (!isIncludeAverage)
                    return chunk;
                return __spreadArray(__spreadArray([], chunk, true), [
                    {
                        id: "summary_".concat(index + 1),
                        month: chunkSize * (index + 1) - 1 + 0.1,
                        percent: mean(watchShrinkageByName[key][index]).toFixed(2),
                    },
                ], false);
            })
                .value();
        })
            .value();
    }, [shrinkageData, watchShrinkageByName, isIncludeAverage, chunkSize]);
    // Build category groups
    var categoryGroups = useMemo(function () {
        var groups = Object.entries(shrinkageDataWithTotal).reduce(function (acc, _a) {
            var _b, _c, _d, _f, _g, _h, _j;
            var key = _a[0], values = _a[1];
            var firstShrinkage = (_b = shrinkageData[key]) === null || _b === void 0 ? void 0 : _b[0];
            var category = (_c = firstShrinkage === null || firstShrinkage === void 0 ? void 0 : firstShrinkage.shrinkageCategory) !== null && _c !== void 0 ? _c : 'uncategorized';
            var rawFactorName = (_d = firstShrinkage === null || firstShrinkage === void 0 ? void 0 : firstShrinkage.name) !== null && _d !== void 0 ? _d : key;
            var factorName = stripDisplaySuffix(rawFactorName);
            var subcategory = ((_f = firstShrinkage === null || firstShrinkage === void 0 ? void 0 : firstShrinkage.shrinkageSubcategory) === null || _f === void 0 ? void 0 : _f.trim()) || null;
            var isPlainRow = !subcategory;
            var groupKey = isPlainRow ? PLAIN_ROWS_KEY : factorName;
            var categoryGroup = ((_g = acc[category]) !== null && _g !== void 0 ? _g : (acc[category] = { factorOrder: [], factors: {} }));
            if (!categoryGroup.factors[groupKey]) {
                categoryGroup.factorOrder.push(groupKey);
                categoryGroup.factors[groupKey] = [];
            }
            categoryGroup.factors[groupKey].push({
                key: getShrinkageDisplayKey(key, (_h = firstShrinkage === null || firstShrinkage === void 0 ? void 0 : firstShrinkage.queueLocationId) !== null && _h !== void 0 ? _h : queueLocationId),
                name: rawFactorName,
                shrinkageType: (_j = firstShrinkage === null || firstShrinkage === void 0 ? void 0 : firstShrinkage.shrinkageType) !== null && _j !== void 0 ? _j : null,
                values: values,
                category: category,
                subcategory: subcategory,
            });
            return acc;
        }, {});
        for (var _i = 0, EMPTY_CATEGORY_ORDER_1 = EMPTY_CATEGORY_ORDER; _i < EMPTY_CATEGORY_ORDER_1.length; _i++) {
            var category = EMPTY_CATEGORY_ORDER_1[_i];
            if (!groups[category]) {
                groups[category] = { factorOrder: [], factors: {} };
            }
        }
        var categoryOrder = __spreadArray(__spreadArray([], CATEGORY_ORDER.filter(function (c) { return c in groups; }), true), Object.keys(groups).filter(function (c) { return !CATEGORY_ORDER.includes(c); }), true);
        return { groups: groups, categoryOrder: categoryOrder };
    }, [shrinkageDataWithTotal, shrinkageData, queueLocationId]);
    var handleCheckbox = useCallback(function (index, checked) {
        setSelectedRows === null || setSelectedRows === void 0 ? void 0 : setSelectedRows(function (previousRows) {
            return checked ? __spreadArray(__spreadArray([], previousRows, true), [index], false) : previousRows.filter(function (row) { return row !== index; });
        });
    }, [setSelectedRows]);
    var getCategoryLabel = function (category) { return t("category.".concat(category), { defaultValue: category }); };
    var _j = useState({}), collapsedCategories = _j[0], setCollapsedCategories = _j[1];
    var handleToggleCategory = useCallback(function (category) {
        setCollapsedCategories(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[category] = !prev[category], _a)));
        });
    }, []);
    var getFormRowIndex = useCallback(function (values) {
        var firstMonthCell = values.find(function (cell) { return cell.month % 1 === 0; });
        if (!firstMonthCell)
            return -1;
        return findShrinkageFormRowIndex(watchShrinkageData === null || watchShrinkageData === void 0 ? void 0 : watchShrinkageData[firstMonthCell.month], firstMonthCell);
    }, [watchShrinkageData]);
    var getFactorTarget = useCallback(function (row) {
        var _a, _b, _c, _d, _f, _g, _h, _j;
        var firstMonthCell = (_a = row.values.find(function (cell) { return isShrinkageCell(cell) && cell.month % 1 === 0; })) !== null && _a !== void 0 ? _a : row.values.find(isShrinkageCell);
        if (!firstMonthCell)
            return null;
        return {
            id: firstMonthCell.id,
            queueId: firstMonthCell.queueId,
            queueLocationId: (_b = firstMonthCell.queueLocationId) !== null && _b !== void 0 ? _b : null,
            name: stripDisplaySuffix((_c = firstMonthCell.name) !== null && _c !== void 0 ? _c : row.name),
            shrinkageCategory: getShrinkageCategory((_d = firstMonthCell.shrinkageCategory) !== null && _d !== void 0 ? _d : row.category),
            shrinkageSubcategory: (_g = (_f = firstMonthCell.shrinkageSubcategory) !== null && _f !== void 0 ? _f : row.subcategory) !== null && _g !== void 0 ? _g : null,
            shrinkageType: ((_j = (_h = firstMonthCell.shrinkageType) !== null && _h !== void 0 ? _h : row.shrinkageType) !== null && _j !== void 0 ? _j : null),
        };
    }, []);
    var visibleDataRowIndices = useMemo(function () {
        return Object.values(categoryGroups.groups)
            .flatMap(function (categoryGroup) { return Object.values(categoryGroup.factors).flat(); })
            .map(function (_a) {
            var values = _a.values;
            return getFormRowIndex(values);
        })
            .filter(function (index) { return index !== undefined && index >= 0; });
    }, [categoryGroups.groups, getFormRowIndex]);
    var visibleDataRowIdentities = useMemo(function () {
        return Object.values(categoryGroups.groups)
            .flatMap(function (categoryGroup) { return Object.values(categoryGroup.factors).flat(); })
            .map(function (_a) {
            var values = _a.values;
            return getShrinkageTotalIdentity(values, queueLocationId);
        })
            .filter(function (identity) { return !!identity; });
    }, [categoryGroups.groups, queueLocationId]);
    var renderFactorRow = function (row, formRowIdx, isSubFactor) {
        var _a;
        if (isSubFactor === void 0) { isSubFactor = false; }
        var name = row.name, shrinkageType = row.shrinkageType, values = row.values, subcategory = row.subcategory;
        var currentRowIdx = formRowIdx;
        var displayName = name;
        if (isSubFactor && subcategory) {
            displayName = subcategory;
        }
        var typeIcon = shrinkageType ? (_jsx(FontAwesomeIcon, { icon: shrinkageType === 'unplanned' ? faBanParking : faCircleParking, size: "sm" })) : null;
        var handleEditFactor = function (event) {
            event.preventDefault();
            event.stopPropagation();
            if (disabled)
                return;
            var factorTarget = getFactorTarget(row);
            if (factorTarget)
                onEditFactor === null || onEditFactor === void 0 ? void 0 : onEditFactor(factorTarget);
        };
        // Id of the saved shrinkage row that carries this name; averaged multiskill rows have none.
        var factorId = (_a = getFactorTarget(row)) === null || _a === void 0 ? void 0 : _a.id;
        var factorNameLink = isReview || disabled ? (_jsx(Typography, __assign({ sx: { textAlign: 'left', fontWeight: 'normal' } }, { children: _jsx(RumEntityLabel, __assign({ kind: "shrinkageFactor", id: factorId }, { children: displayName })) }))) : (_jsx(StyledLink, __assign({ type: "button", underline: "always", disabled: disabled, showCheckbox: showCheckbox, onClick: disabled ? undefined : handleEditFactor, sx: { textAlign: 'left', fontWeight: 'normal' }, "data-dd-action-name": typeof factorId === 'number'
                ? "Edit shrinkage factor: ".concat(rumEntityName('shrinkageFactor', factorId))
                : undefined }, { children: _jsx(RumEntityLabel, __assign({ kind: "shrinkageFactor", id: factorId, hasActionName: false }, { children: displayName })) })));
        return (_jsxs(TableRow, { children: [_jsx(FactorNameCell, __assign({ stickySizes: STICKIES, align: "left", isSubFactor: isSubFactor, nestLevel: isSubFactor ? 3 : 2 }, { children: _jsxs(Stack, __assign({ direction: "row", alignItems: "center", justifyContent: "space-between" }, { children: [showCheckbox ? (_jsx(FormControlLabel, { control: _jsx(Checkbox, { sx: { py: 0 }, disableRipple: true, size: "small", checked: selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.includes(currentRowIdx), onChange: function (_e, checked) { return handleCheckbox(currentRowIdx, checked); } }), label: factorNameLink })) : (factorNameLink), typeIcon] })) })), values.map(function (cell, cellIdx, arr) {
                    var _a, _b, _c;
                    var isAverageColumn = cell.month % 1 !== 0;
                    var formRowIndex = findShrinkageFormRowIndex(watchShrinkageData === null || watchShrinkageData === void 0 ? void 0 : watchShrinkageData[cell.month], cell);
                    var cellContent = null;
                    var isInputField = formRowIndex >= 0;
                    var businessDays = getBusinessDays(planType, workingDaysPerWeek !== null && workingDaysPerWeek !== void 0 ? workingDaysPerWeek : 0, periods, cell.month);
                    if (isAverageColumn) {
                        var displayValue = getShrinkageDisplayValue(cell.percent, unitMode, paidHoursPerDay, businessDays);
                        var suffix = (_a = getDisplaySuffix(unitMode)) !== null && _a !== void 0 ? _a : '';
                        var formattedValue = displayValue === null ? null : Number(displayValue).toFixed(2);
                        cellContent = formattedValue === null ? null : "".concat(formattedValue).concat(suffix);
                    }
                    else if (isInputField) {
                        cellContent = (_jsx(FormField, __assign({ disableHelperText: true, required: true, name: "shrinkages.".concat(cell.month, ".").concat(formRowIndex, ".percent") }, { children: _jsx(ShrinkageUnitInput, { disabled: disabled, unit: unitMode, paidHoursPerDay: paidHoursPerDay, businessDays: businessDays }) })));
                    }
                    var hierarchyBackgroundColor = isAverageColumn || isSubFactor ? TABLE_HIERARCHY_COLORS.lightGray : TABLE_HIERARCHY_COLORS.lightBlueGray;
                    var backgroundColor = isInputField
                        ? TABLE_HIERARCHY_COLORS.white
                        : "".concat(hierarchyBackgroundColor, " !important");
                    return (_jsx(StyledTableCell, __assign({ greyColor: isAverageColumn || disabled, padding: "none", highlightBorder: arr.length - 1 !== cellIdx && (isIncludeAverage ? isAverageColumn : !((cellIdx + 1) % chunkSize)), sx: {
                            backgroundColor: backgroundColor,
                        } }, { children: cellContent }), "".concat((_b = cell.id) !== null && _b !== void 0 ? _b : 'cell', "-").concat(cell.month, "-").concat((_c = normalizeQueueLocationId(getCellQueueLocationId(cell))) !== null && _c !== void 0 ? _c : 'queue')));
                })] }, formRowIdx));
    };
    return (_jsxs(TableBody, { children: [categoryGroups.categoryOrder.map(function (category) {
                var categoryGroup = categoryGroups.groups[category];
                var rows = categoryGroup.factorOrder.flatMap(function (factorKey) { return categoryGroup.factors[factorKey]; });
                var dataRowIndices = rows
                    .map(function (_a) {
                    var values = _a.values;
                    return getFormRowIndex(values);
                })
                    .filter(function (index) { return index !== undefined && index >= 0; });
                var dataRowIdentities = rows
                    .map(function (_a) {
                    var values = _a.values;
                    return getShrinkageTotalIdentity(values, queueLocationId);
                })
                    .filter(function (identity) { return !!identity; });
                var categoryLabel = getCategoryLabel(category);
                var isExpanded = !collapsedCategories[category];
                var isUncategorized = category === 'uncategorized';
                var hasChildren = rows.length > 0;
                return (_jsxs(React.Fragment, { children: [!isUncategorized && (_jsx(ShrinkageCategoryRow, { categoryLabel: categoryLabel, columnCount: columnCount, isExpanded: isExpanded, onToggle: function () { return handleToggleCategory(category); }, hasChildren: hasChildren, chunkSize: chunkSize, isIncludeAverage: !!isIncludeAverage })), (isUncategorized || isExpanded) &&
                            categoryGroup.factorOrder.map(function (factorKey) {
                                var factorRows = categoryGroup.factors[factorKey];
                                var isFactorGroup = factorKey !== PLAIN_ROWS_KEY;
                                var factorDataRowIndices = factorRows
                                    .map(function (_a) {
                                    var values = _a.values;
                                    return getFormRowIndex(values);
                                })
                                    .filter(function (index) { return index !== undefined && index >= 0; });
                                var factorDataRowIdentities = factorRows
                                    .map(function (_a) {
                                    var values = _a.values;
                                    return getShrinkageTotalIdentity(values, queueLocationId);
                                })
                                    .filter(function (identity) { return !!identity; });
                                var parentFactorTarget = undefined;
                                if (isFactorGroup && factorRows[0]) {
                                    var target = getFactorTarget(factorRows[0]);
                                    if (target) {
                                        parentFactorTarget = __assign(__assign({}, target), { id: undefined, name: factorKey, shrinkageSubcategory: null, shrinkageType: null, isParentFactor: true });
                                    }
                                }
                                return (_jsxs(React.Fragment, { children: [isFactorGroup && (_jsx(ShrinkageSubcategoryTotalRow, { addColumns: addColumns, label: factorKey, watchShrinkage: watchShrinkageData, dataRowIndices: factorDataRowIndices, dataRowIdentities: factorDataRowIdentities, unitMode: unitMode, paidHoursPerDay: paidHoursPerDay, workingDaysPerWeek: workingDaysPerWeek, disabled: disabled, isReview: isReview, onEditFactor: onEditFactor, factorTarget: parentFactorTarget })), factorRows.map(function (row) {
                                            var formRowIdx = getFormRowIndex(row.values);
                                            if (formRowIdx < 0)
                                                return null;
                                            return renderFactorRow(row, formRowIdx, isFactorGroup);
                                        })] }, "".concat(category, "-").concat(factorKey)));
                            }), !isUncategorized && (_jsx(ShrinkageTotalRow, { addColumns: addColumns, label: t('totalCategory', { category: categoryLabel }), watchShrinkage: watchShrinkageData, dataRowIndices: dataRowIndices, dataRowIdentities: dataRowIdentities, isSubtotal: true, unitMode: unitMode, paidHoursPerDay: paidHoursPerDay, workingDaysPerWeek: workingDaysPerWeek }))] }, category));
            }), _jsx(ShrinkageTotalRow, { addColumns: addColumns, label: t('totalOverall'), watchShrinkage: watchShrinkageData, dataRowIndices: visibleDataRowIndices, dataRowIdentities: visibleDataRowIdentities, isSubtotal: false, unitMode: unitMode, paidHoursPerDay: paidHoursPerDay, workingDaysPerWeek: workingDaysPerWeek })] }));
};
export var ShrinkageTable = function (_a) {
    var setSelectedRows = _a.setSelectedRows, selectedRows = _a.selectedRows, queueLocationId = _a.queueLocationId, isReview = _a.isReview, unitModeProp = _a.unitMode, setUnitModeProp = _a.setUnitMode, isUnitModeEnabledProp = _a.isUnitModeEnabled, props = __rest(_a, ["setSelectedRows", "selectedRows", "queueLocationId", "isReview", "unitMode", "setUnitMode", "isUnitModeEnabled"]);
    var capacityPermission = useEffectiveCapacityPermission();
    var _b = useState(false), quickInputOpen = _b[0], setQuickInputOpen = _b[1];
    var _c = useState(null), editFactor = _c[0], setEditFactor = _c[1];
    var allKeys = useAllFormRowIndices(queueLocationId);
    var _d = useState(ShrinkageUnitMode.Percent), localUnitMode = _d[0], setLocalUnitMode = _d[1];
    var _f = useShrinkageStaffSettings(queueLocationId), paidHoursPerDay = _f.paidHoursPerDay, workingDaysPerWeek = _f.workingDaysPerWeek;
    var unitMode = unitModeProp !== null && unitModeProp !== void 0 ? unitModeProp : localUnitMode;
    var setUnitMode = setUnitModeProp !== null && setUnitModeProp !== void 0 ? setUnitModeProp : setLocalUnitMode;
    var isUnitModeEnabled = isUnitModeEnabledProp !== null && isUnitModeEnabledProp !== void 0 ? isUnitModeEnabledProp : (paidHoursPerDay != null && workingDaysPerWeek != null);
    useEffect(function () {
        if (!isUnitModeEnabled && unitMode !== ShrinkageUnitMode.Percent) {
            setUnitMode(ShrinkageUnitMode.Percent);
        }
    }, [isUnitModeEnabled, setUnitMode, unitMode]);
    var handleSelectAllChange = useCallback(function (e, checked) {
        setSelectedRows === null || setSelectedRows === void 0 ? void 0 : setSelectedRows(checked ? allKeys : []);
    }, [allKeys, setSelectedRows]);
    var isPlanLocked = useIsPlanLocked();
    var isDisabled = capacityPermission !== StatusPermission.Edit || isPlanLocked;
    var isQuickInputDisabled = isDisabled || allKeys.length === 0;
    return (_jsxs(_Fragment, { children: [_jsx(TableContainer, __assign({ topScroll: true }, { children: _jsxs(Table, { children: [_jsx(ShrinkageTableHeader, __assign({}, props, { disabled: isQuickInputDisabled, selectAll: allKeys.length === (selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.length), handleSelectAllChange: handleSelectAllChange, onQuickInputClick: function () { return setQuickInputOpen(true); }, isReview: isReview, unitMode: unitMode, setUnitMode: setUnitMode, isUnitModeEnabled: isUnitModeEnabled })), _jsx(ShrinkageTableBody, __assign({}, props, { selectedRows: selectedRows, setSelectedRows: setSelectedRows, disabled: isDisabled, queueLocationId: queueLocationId, onEditFactor: setEditFactor, isReview: isReview, unitMode: unitMode }))] }) })), !isReview && (_jsxs(_Fragment, { children: [_jsx(ShrinkageQuickInputDialog, { open: quickInputOpen, onClose: function () { return setQuickInputOpen(false); }, queueLocationId: queueLocationId, unitMode: unitMode, setUnitMode: setUnitMode, isUnitModeEnabled: isUnitModeEnabled }), _jsx(EditShrinkageFactorDialog, { open: !!editFactor, factor: editFactor, onClose: function () { return setEditFactor(null); } })] }))] }));
};
